// src/contexts/PreferencesContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';

/**
 * PreferencesContext enforces Cloud-only mode in production.
 * In dev, allows selecting Local vs Cloud and persisting model;
 * In prod, locks to Cloud with an empty model (server will apply default).
 */
const PreferencesContext = createContext();

export function PreferencesProvider({ children }) {
  const isProd = process.env.NODE_ENV === 'production';

  // ─── State: modality ──────────────────────────────────────────────────
  // Dev: loaded from localStorage; Prod: always 'Cloud'
  const [modality, setModalityRaw] = useState(() =>
    isProd ? 'Cloud' : localStorage.getItem('modality') || 'Local'
  );
  // ─── State: apiKey and model ──────────────────────────────────────────
  // Dev: loaded from localStorage; Prod: blank (server uses defaults)
  const [apiKey, setApiKeyRaw] = useState(() =>
    isProd ? '' : localStorage.getItem('apiKey') || ''
  );
  const [model, setModelRaw] = useState(() =>
    isProd ? '' : localStorage.getItem('model') || ''
  );

  // Persist only in development
  useEffect(() => {
    if (!isProd) localStorage.setItem('modality', modality);
  }, [modality, isProd]);
  useEffect(() => {
    if (!isProd) localStorage.setItem('apiKey', apiKey);
  }, [apiKey, isProd]);
  useEffect(() => {
    if (!isProd) localStorage.setItem('model', model);
  }, [model, isProd]);

  // No-ops in production
  const setModality = isProd ? () => {} : setModalityRaw;
  const setApiKey   = isProd ? () => {} : setApiKeyRaw;
  const setModel    = isProd ? () => {} : setModelRaw;

  return (
    <PreferencesContext.Provider
      value={{ modality, setModality, apiKey, setApiKey, model, setModel }}
    >
      {children}
    </PreferencesContext.Provider>
  );
}

export default function usePreferences() {
  const ctx = useContext(PreferencesContext);
  if (!ctx) {
    throw new Error('usePreferences must be used within a PreferencesProvider');
  }
  return ctx;
}
